import { Calendar, Globe, Link as LinkIcon, MapPin } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DistrictData } from "@/types/review";

interface DistrictCardProps {
  district: DistrictData;
  selectedUrl: string;
  onUrlSelect: (url: string) => void;
  showPreview?: boolean;
}

export default function DistrictCard({
  district,
  selectedUrl,
  onUrlSelect,
  showPreview = true,
}: DistrictCardProps) {
  const availableUrls = [
    { label: "Primary URL", url: district.current_ats_url },
    { label: "URL 1", url: district.ats_url_1 },
    { label: "URL 2", url: district.ats_url_2 },
  ].filter((item) => item.url && item.url.trim() !== "");

  const formatDate = (timestamp: string) => {
    if (!timestamp) return "Unknown";
    try {
      return new Date(timestamp).toLocaleDateString();
    } catch {
      return "Invalid date";
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-gray-500" />
            <span>{district.district_name}</span>
            <Badge variant="secondary">{district.state_abbreviation}</Badge>
          </div>
          <Badge variant="outline">ID: {district.nces_id}</Badge>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* District Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            <span>Website: {district.district_website || "Not provided"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>Discovered: {formatDate(district.search_timestamp)}</span>
          </div>
        </div>

        {/* URLs Section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4" />
            <span className="font-medium">
              Job Board URLs ({district.job_boards_count} found)
            </span>
          </div>

          {availableUrls.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-yellow-800 text-sm">
                ⚠️ No job board URLs found for this district
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {availableUrls.map((urlItem, index) => (
                <div
                  key={index}
                  className={`border rounded-md p-3 cursor-pointer transition-colors ${
                    selectedUrl === urlItem.url
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                  onClick={() => onUrlSelect(urlItem.url)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                        {urlItem.label}
                      </div>
                      <div className="text-sm font-mono text-blue-600 break-all">
                        {urlItem.url}
                      </div>
                    </div>
                    {selectedUrl === urlItem.url && (
                      <Badge className="ml-2">Selected</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* URL Preview */}
        {showPreview && selectedUrl && (
          <div className="space-y-2">
            <div className="font-medium text-sm">Preview:</div>
            <div className="border rounded-md overflow-hidden bg-gray-50">
              <iframe
                src={selectedUrl}
                className="w-full h-[50vh]"
                title={`Preview of ${district.district_name} job board`}
                sandbox="allow-same-origin allow-scripts"
                onError={() => console.log("iframe failed to load")}
              />
            </div>
            <p className="text-xs text-gray-500">
              If the preview doesn&apos;t load, use &quot;Open in New Tab&quot;
              to review the URL directly.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
